"use client";

import React from "react";

import type { ReactNode } from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  VStack,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useToast,
  Box,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  ButtonGroup,
} from "@chakra-ui/react";
import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import { emailAgentService } from "../../email-services/agentService";

interface EmailModelSelectProps {
  agentId?: string;
  initialModel?: string;
  initialTemperature?: number;
  value?: string;
  onChange?: (model: string) => void;
  onModelUpdate?: (model: string) => void;
  onTemperatureChange?: (temperature: number) => void;
}

const modelOptions = [
  { name: "GPT 5", value: "gpt-5", inputPrice: "$1.25", outputPrice: "$10.00" },
  {
    name: "GPT 5 Mini",
    value: "gpt-5-mini",
    inputPrice: "$0.25",
    outputPrice: "$2.00",
  },
  {
    name: "GPT 5 Nano",
    value: "gpt-5-nano",
    inputPrice: "$0.05",
    outputPrice: "$0.40",
  },
  {
    name: "GPT 4.1",
    value: "gpt-4.1",
    inputPrice: "$2.00",
    outputPrice: "$8.00",
  },
  {
    name: "GPT 4.1 Mini",
    value: "gpt-4.1-mini",
    inputPrice: "$0.40",
    outputPrice: "$1.60",
  },
  {
    name: "GPT 4.1 Nano",
    value: "gpt-4.1-nano",
    inputPrice: "$0.10",
    outputPrice: "$0.40",
  },
  {
    name: "GPT 4o",
    value: "gpt-4o",
    inputPrice: "$2.50",
    outputPrice: "$10.00",
  },
  {
    name: "GPT 4o Mini",
    value: "gpt-4o-mini",
    inputPrice: "$0.15",
    outputPrice: "$0.60",
  },
  { name: "o1", value: "o1", inputPrice: "$15.00", outputPrice: "$60.00" },
  { name: "o3", value: "o3", inputPrice: "$2.00", outputPrice: "$8.00" },
  {
    name: "o4 Mini",
    value: "o4-mini",
    inputPrice: "$1.10",
    outputPrice: "$4.40",
  },
  {
    name: "o3 Mini",
    value: "o3-mini",
    inputPrice: "$1.10",
    outputPrice: "$4.40",
  },
  {
    name: "o1 Mini",
    value: "o1-mini",
    inputPrice: "$1.10",
    outputPrice: "$4.40",
  },
];

export const EmailModelSelect: React.FC<EmailModelSelectProps> = React.memo(
  ({
    agentId,
    initialModel,
    initialTemperature,
    value,
    onChange,
    onModelUpdate,
    onTemperatureChange,
  }) => {
    const isControlled = value !== undefined && onChange !== undefined;
    const [selectedModel, setSelectedModel] = useState(
      isControlled ? value : initialModel || "gpt-4o-mini"
    );
    const [temperature, setTemperature] = useState(initialTemperature ?? 0.7);
    const [tempTemperature, setTempTemperature] = useState(
      initialTemperature ?? 0.7
    );
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const toast = useToast();

    useEffect(() => {
      if (initialTemperature !== undefined) {
        setTemperature(initialTemperature);
        setTempTemperature(initialTemperature);
      }
    }, [initialTemperature]);

    useEffect(() => {
      if (isControlled) {
        setSelectedModel(value);
      }
    }, [value, isControlled]);

    const handleModelChange = useCallback(
      async (modelValue: string) => {
        if (isControlled) {
          onChange(modelValue);
          return;
        }

        if (!agentId) return;

        try {
          await emailAgentService.update(agentId, { llm: modelValue });
          setSelectedModel(modelValue);
          if (onModelUpdate) onModelUpdate(modelValue);
          toast({
            title: "Model updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          toast({
            title: "Error updating model",
            description: error?.message || "An unexpected error occurred",
            status: "error",
            duration: 3000,
          });
        }
      },
      [agentId, onModelUpdate, toast, isControlled, onChange]
    );

    const handleTemperatureSave = useCallback(async () => {
      if (isControlled) {
        setTemperature(tempTemperature);
        setIsSettingsOpen(false);
        if (onTemperatureChange) {
          onTemperatureChange(tempTemperature);
        }
        return;
      }

      if (!agentId) return;

      try {
        await emailAgentService.update(agentId, {
          llm_temperature: tempTemperature,
        });
        setTemperature(tempTemperature);
        setIsSettingsOpen(false);
        toast({
          title: "Temperature updated successfully",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        toast({
          title: "Error updating temperature",
          description: error?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    }, [agentId, tempTemperature, toast, isControlled, onTemperatureChange]);

    const handleSettingsCancel = () => {
      setTempTemperature(temperature);
      setIsSettingsOpen(false);
    };

    const getModelIcon = (): ReactNode => {
      return (
        <Box as="span" w="20px" h="20px">
          <svg
            viewBox="0 0 24 24"
            fill="black"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 0-.071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
          </svg>
        </Box>
      );
    };

    const selectedModelOption =
      modelOptions.find((m) => m.value === selectedModel) || modelOptions[7];

    return (
      <HStack spacing={1}>
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            rightIcon={<ChevronDownIcon />}
            colorScheme="orange"
            borderColor="orange.400"
            color="gray.800"
            _hover={{ bg: "orange.50", borderColor: "orange.500" }}
            _active={{ bg: "orange.100", borderColor: "orange.600" }}
            _focusVisible={{
              borderColor: "orange.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
            }}
          >
            <HStack spacing={2}>
              {getModelIcon()}
              <Text color="gray.800">{selectedModelOption.name}</Text>
            </HStack>
          </MenuButton>

          <MenuList
            maxH="400px"
            overflowY="auto"
            borderColor="orange.200"
            _focusWithin={{
              borderColor: "orange.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)",
            }}
            sx={{
              /* Firefox */
              scrollbarWidth: "thin",
              scrollbarColor: "var(--chakra-colors-orange-400) transparent",

              /* Chrome / Safari / Edge */
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-orange-400)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "var(--chakra-colors-orange-500)",
              },
            }}
          >
            {modelOptions.map((model) => (
              <MenuItem
                key={model.value}
                onClick={() => handleModelChange(model.value)}
                _hover={{ bg: "orange.50" }}
                _focus={{ bg: "orange.50" }}
                _active={{ bg: "orange.100" }}
              >
                <HStack spacing={2} w="full" justify="space-between">
                  <HStack>
                    {getModelIcon()}
                    <Text>{model.name}</Text>
                  </HStack>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <Popover
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          placement="bottom-start"
          closeOnBlur={false}
        >
          <PopoverTrigger>
            <IconButton
              aria-label="Model settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="outline"
              colorScheme="orange"
              borderColor="orange.400"
              color="orange.600"
              _hover={{ bg: "orange.50", borderColor: "orange.500" }}
              _active={{ bg: "orange.100", borderColor: "orange.600" }}
              _focusVisible={{
                borderColor: "orange.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
              }}
              onClick={() => setIsSettingsOpen(true)}
            />
          </PopoverTrigger>

          <PopoverContent
            w="300px"
            borderColor="orange.200"
            _focusWithin={{
              borderColor: "orange.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)",
            }}
          >
            <PopoverBody>
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" fontWeight="medium">
                  Temperature: {tempTemperature.toFixed(2)}
                </Text>

                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  value={tempTemperature}
                  onChange={setTempTemperature}
                  colorScheme="orange"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>

                <ButtonGroup justifyContent="flex-end" size="sm">
                  <Button
                    variant="ghost"
                    onClick={handleSettingsCancel}
                    color="black"
                    _hover={{ bg: "gray.100" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="orange"
                    bg="orange.500"
                    _hover={{ bg: "orange.600" }}
                    onClick={handleTemperatureSave}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    );
  }
);

export default EmailModelSelect;
